import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Address } from '@models/address';
import { isAndroidApp } from '@utils/utils';
import * as moment from 'moment';
import { from, of, throwError } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';
import {
  LANGUAGE,
  SMS
} from '../../../modules/receipt-payment/receipt-payment-constants';
import {
  ContactInfoField,
  DefaultRequiredPositions,
  ProfileBusinessInfo,
  ProfileProduct,
  ProfileUserInfo,
  Question,
  SigningKey,
  SigningKeyPositions,
  SigningKeyUpdate,
  UpdateBusinessLegalRepresentative,
  UpdatePassword,
  UpdateSecurityQuestion
} from '../../models/profile';
import { Environment } from '../common';
import { ProfileContextService } from '../context/profile-context.service';
import { AuthService } from '../core/auth/auth.service';
import { BaseService } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';
const USER_PROFILE_BLOCKING_TIME = 20;
const COMPANY_PROFILE_BLOCKING_TIME = 60;
@Injectable({
  providedIn: 'root'
})
export class ProfileService {

  constructor(
    private base: BaseService,
    private mmobileHelper: MMobileHelperService,
    private http: HttpClient,
    private auth: AuthService,
    private or: OperationRegisterService,
    private profileContext: ProfileContextService,
    private platform: Platform
  ) {}

  getUserInfo() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.get<ProfileUserInfo>(url, headers);
  }

  getPrivateAreaToken() {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/privateAreaToken`;
    const body = { token: this.auth.getState().user.token };
    return this.http.post < { token: string }>(url, body);
  }

  getCompanyInfo(token: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.get<ProfileBusinessInfo>(url, { headers });
  }

  getQuestionList() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/questions`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Question[]>(url, headers);
  }

  getContractAcceptance(acceptanceDate) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/contract`;

    const params = Object.assign( {contractAcceptanceDate: acceptanceDate}, this.or.getDownloadContractROParams());
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });

    return this.http
      .get<{ pdf: string }>(url, { ...headers, params: params})
  }

  checkOldPassword(password: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/password/validation`;
    const body = { password };
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.setProfileParams('Contraseña', '');
    return this.http.post(url, body, { ...headers, params });
  }

  changePassword(passwords: UpdatePassword) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/password`;
    const body = passwords;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.setProfileParams('Contraseña', '');
    return this.http.post<UpdatePassword>(url, body, { ...headers, params });
  }

  changeSecurityQuestion(data: UpdateSecurityQuestion) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/question`;
    const body = data;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.setProfileParams('Pregunta de seguridad', '');
    return this.http.post<UpdateSecurityQuestion>(url, body, {
      ...headers,
      params
    });
  }

  saveUserInfo(email: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile`;
    const body = { email };
    const headers = this.base.getPrivateOIMHeaders();
    const emailProductRO = 'Email';
    const params = this.or.setProfileParams(emailProductRO, '');
    return this.http.post<string>(url, body, { ...headers, params });
  }

  saveUserBirthDate(birthdate: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/birthdate`;
    const body = { birthdate };
    const headers = this.base.getPrivateOIMHeaders();
    const birthdateProductRO = 'Fecha nacimiento';
    const params = this.or.setProfileParams(birthdateProductRO, '');
    return this.http.post<string>(url, body, { ...headers, params });
  }

  saveCompanyLegalRepresentativeInfo(token: string, legalRepresentative: UpdateBusinessLegalRepresentative) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/legalRepresentative`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post<{token: string}>(url, legalRepresentative, { headers });
  }

  saveCompanyAddress(token: string, companyAddress: Address) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/address`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    delete companyAddress.province;
    return this.http.post(url, companyAddress, { headers });
  }

  saveChangeAddress(address) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/address`;
    const body = { address };
    body.address.postalCode = +address.postalCode;
    const headers = this.base.getPrivateOIMHeaders();
    const params = this.or.setProfileParams('Dirección de contacto', '');
    return this.http.post<any>(url, body, { ...headers, params });
  }

  saveChangePhone(data: ContactInfoField, token: string) {
    const user = this.auth.getState().user;
    const url = this.getUrl('mets-profile-sms', 'middlewareChangePhoneNumber');
    const body = data;
    const headers = this.base.getPublicProfilePinHeaders(
      data.phones[0],
      token,
      user.token
    );
    const params = this.or.setProfileParams('', '');
    return this.http.post<ContactInfoField>(url, body, { ...headers, params });
  }

  changePhonePinCode(
    documentNumber: string,
    userName: string,
    oldPhoneNumber: string,
    newPhoneNumber: string,
    userToken: string
  ) {
    const pin = this.getProfilePin(
      documentNumber,
      userName,
      newPhoneNumber,
      userToken,
      'mets-profile-sms',
      oldPhoneNumber
    );
    return from(pin).pipe(
      flatMap(data => {
        if (data) {
          return this.http.post(data.url, data.requestBody, data.headers);
        } else {
          throwError('err');
        }
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  validatePinCode(
    newPhoneNumber: string,
    pinCode: string,
    isSigningKey: boolean,
    isCreateKeyOperation?: boolean
  ) {
    const appKey = isSigningKey
      ? isCreateKeyOperation
        ? 'mets-signingkey-cr-profile-sms'
        : 'mets-signingkey-ca-profile-sms'
      : 'mets-profile-sms';
    const data = this.getValidatePinCode(newPhoneNumber, pinCode, appKey);
    return this.http.post(data.url, data.requestBody, data.headers);
  }

  checkProfileBlockByTimeStampIsExpired() {
    return this.checkIsOperativeBlocked(
      this.profileContext.changePhoneBlockedByTimeStamp.getValue()
    );
  }

  // BUSINESSES CHANGE PHONE SERVICES

  validateCompanyOldPhone(token: string, phone: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/phone/validate`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    const body = {
      currentPhone: phone
    };
    return this.http.post<{ token: string }>(url, body, { headers });
  }

  sendPinToNewCompanyPhone(token: string, newPhone: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/phone/pin/send`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post<{ token: string }>(url, { newPhone }, { headers });
  }

  validateCompanyPinCode(token: string, pin: number) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/phone/pin/validate`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post<{ token: string }>(url, {pin}, { headers });
  }

  changeCompanyPassword(token: string, currentPassword: string, newPassword?: string) {
    const url = `${this.base.getAwsBaseUrl()}/metsintegration/private/profile/password`;
    const headers = new HttpHeaders()
      .append('Authorization', token);
    return this.http.post(url, { newPassword, currentPassword }, { headers });
  }
  //// SIGNING KEY
  validateCurrentSigningKey(profileKey: SigningKey[]) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/signingKey/validate`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const body = { positions: profileKey };
    return this.http.post(url, body, headers);
  }

  getSigningKeyRequiredPositions() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/signingKey/validationPositions`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.get<SigningKeyPositions>(url, headers).pipe(
      map(positions => positions.positions),
      catchError(() => of(DefaultRequiredPositions))
    );
  }

  blockSigningKey() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/signingKey/block`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.post(url, {}, headers);
  }

  cancelSigningKey(token: string) {
    const user = this.auth.getState().user;
    const url = this.getUrl(
      'mets-signingkey-ca-profile-sms',
      'middlewareCancelSigningKey'
    );
    const headers = this.base.getPublicProfilePinHeaders(
      user.phone,
      token,
      user.token
    );
    const params = this.or.setSigningKeyParams(ProfileProduct.CANCEL);
    return this.http.post(url, {}, { ...headers, params });
  }

  modifiedSigningKey(currentKeyPositions: SigningKey[], newKey: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/clients/profile/signingKey`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const body = {
      currentKeyPositions,
      key: newKey
    } as SigningKeyUpdate;
    const params = this.or.setSigningKeyParams(ProfileProduct.MODIFIED);
    return this.http.put<SigningKeyUpdate>(url, body, { ...headers, params });
  }

  createSigningKey(token: string, value: string) {
    const user = this.auth.getState().user;
    const url = this.getUrl(
      'mets-signingkey-cr-profile-sms',
      'middlewareCreateSigningKey'
    );
    const headers = this.base.getPublicProfilePinHeaders(
      user.phone,
      token,
      user.token
    );
    const body = { key: value } as SigningKeyUpdate;
    const params = this.or.setSigningKeyParams(ProfileProduct.ASK);
    return this.http.post<SigningKeyUpdate>(url, body, { ...headers, params });
  }

  signingKeyPin(
    documentNumber: string,
    userName: string,
    phoneNumber: string,
    userToken: string,
    isCreateKeyOperation: boolean
  ) {
    const appKey = isCreateKeyOperation
      ? 'mets-signingkey-cr-profile-sms'
      : 'mets-signingkey-ca-profile-sms';
    const pin = this.getProfilePin(
      documentNumber,
      userName,
      phoneNumber,
      userToken,
      appKey
    );
    return from(pin).pipe(
      flatMap(data => {
        if (data) {
          return this.http.post(data.url, data.requestBody, data.headers);
        } else {
          throwError('err');
        }
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  checkSinginKeyBlockByTimeStampIsExpired() {
    return this.checkIsOperativeBlocked(
      this.profileContext.signingKeyBlockedTimeStamp.getValue()
    );
  }

  private async getProfilePin(
    documentNumber: string,
    userName: string,
    phoneNumber: string,
    headerUserToken: string,
    appKey: string,
    oldPhone?: string
  ) {
    const pinUrl = this.mmobileHelper.getRequirePinUrl();
    const headers = this.base.getPrivateOIMHeaders();
    const versionEnv = Environment.config().versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    const body = {
      businessId: documentNumber,
      language: isAndroidApp(this.platform)
        ? LANGUAGE.android
        : LANGUAGE.default,
      userType: SMS,
      userId: phoneNumber,
      userName,
      appKey,
      versionKey,
      validationData: {
        headerUserToken,
        headerVersionId: this.mmobileHelper.getVersionId(),
        oldPhone: oldPhone ? oldPhone : phoneNumber
      }
    };
    return {
      url: pinUrl,
      headers,
      requestBody: body
    };
  }

  private getValidatePinCode(
    newPhoneNumber: string,
    pinCode: string,
    appKey: string
  ) {
    const pinUrl = this.mmobileHelper.getValidatePinUrl();
    const headers = this.base.getPrivateOIMHeaders();
    const versionEnv = Environment.config().versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    const body = {
      pin: pinCode,
      userId: newPhoneNumber,
      appKey,
      versionKey
    };
    return {
      url: pinUrl,
      headers,
      requestBody: body
    };
  }

  private getUrl(appKey: string, endpoint: string) {
    const envConfig = Environment.config();
    const versionEnv = Environment.config().versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    return `${envConfig.mmobileUrl}/services/p/${appKey}/${versionKey}/${endpoint}`;
  }

  private checkIsOperativeBlocked(timestamp: string) {
    let isExpired = true;
    const min = moment().diff(moment(timestamp), 'minutes');
    const blockingTime = this.auth.checkUserIsParticular()
      ? USER_PROFILE_BLOCKING_TIME
      : COMPANY_PROFILE_BLOCKING_TIME;
    if (min < blockingTime) {
      isExpired = false;
    } else {
      isExpired = true;
    }
    return isExpired;
  }
}
